import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import { Spinner } from '@/shared/ui/Spinner';

type Tone = 'neutral' | 'approve' | 'reject';

/** Cor do ícone e realce no hover — independem de haver borda. */
const TONES: Record<Tone, string> = {
  neutral: 'text-ink-muted hover:bg-brand-50 hover:text-brand-700',
  approve: 'text-ink-subtle hover:bg-approved-soft hover:text-approved',
  reject: 'text-ink-subtle hover:bg-rejected-soft hover:text-rejected',
};

/** Borda e sua cor no hover, aplicadas só na variante com contorno. */
const TONE_BORDERS: Record<Tone, string> = {
  neutral: 'border border-line-strong hover:border-brand-300',
  approve: 'border border-line-strong hover:border-approved/40',
  reject: 'border border-line-strong hover:border-rejected/40',
};

interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'title'> {
  /** Obrigatório: o botão só tem ícone, então precisa de nome acessível. */
  label: string;
  /** Texto do tooltip; por padrão repete `label`. Use para encurtar o que o mouse mostra. */
  tooltip?: string;
  icon: ReactNode;
  tone?: Tone;
  loading?: boolean;
  /**
   * Contorno e fundo próprio. `false` deixa o botão transparente, só com o realce
   * no hover — é o que as ações da linha usam, para não desenhar uma caixa branca
   * sobre a linha (que tem fundo próprio no hover).
   */
  bordered?: boolean;
}

export function IconButton({
  label,
  tooltip,
  icon,
  tone = 'neutral',
  loading = false,
  bordered = true,
  className,
  disabled,
  ...props
}: IconButtonProps) {
  return (
    <button
      type={props.type ?? 'button'}
      aria-label={label}
      title={tooltip ?? label}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex size-9 items-center justify-center rounded-lg transition-colors duration-150',
        'disabled:cursor-not-allowed disabled:opacity-50',
        bordered ? cn('bg-surface disabled:hover:bg-surface disabled:hover:border-line-strong', TONE_BORDERS[tone]) : 'bg-transparent',
        TONES[tone],
        className,
      )}
      {...props}
    >
      {loading ? <Spinner className="size-4" /> : <span className="[&>svg]:size-[1.125rem]">{icon}</span>}
    </button>
  );
}
